import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { DOCUMENT } from '@angular/common';
import { Component, Inject, NgZone, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { Router } from '@angular/router';

import { MatSnackBar } from '@angular/material/snack-bar';

import { App, URLOpenListenerEvent } from '@capacitor/app';
import { NavController, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { decodeJwt } from 'jose';
import { Subscription } from 'rxjs';

import { SidebarButton } from './components/navigation/sidebar/sidebar.component';
import { UserToken } from './models/auth.model';
import { Settings } from './models/settings.model';
import { AuthService } from './services/auth.service';
import { AccessFacadeService } from './services/facades/access-facade.service';
import { DeviceFacadeService } from './services/facades/device-facade.service';
import { LocatorsFacadeService } from './services/facades/locators-facade.service';
import { SettingsFacadeService } from './services/facades/settings-facade.service';
import { ServerSentEventHandlerService } from './services/mercure/server-sent-event-handler.service';
import { ThemeService } from './services/theme.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit, OnDestroy {
  public accessToken: UserToken | null = null;
  public selectedColorSchema: string = '';
  public currentBreakpoint: string = '';
  private allSubs: Subscription[] = [];
  public showSidebar: boolean = false;
  public username: string = '';

  public displayNameMap = new Map([
    [Breakpoints.HandsetPortrait, 'handsetPortrait'],
    [Breakpoints.HandsetLandscape, 'handsetLandscape'],
    [Breakpoints.Web, 'web'],
    [Breakpoints.Tablet, 'tablet'],
  ]);

  public constructor(
    @Inject(DOCUMENT) private document: Document,
    private accessFacade: AccessFacadeService,
    private authService: AuthService,
    private breakpointObserver: BreakpointObserver,
    private deviceF: DeviceFacadeService,
    private locatorsF: LocatorsFacadeService,
    private navController: NavController,
    private platform: Platform,
    private renderer: Renderer2,
    private router: Router,
    private settings: SettingsFacadeService,
    private snackBar: MatSnackBar,
    private SSEHService: ServerSentEventHandlerService,
    private themeService: ThemeService,
    private translate: TranslateService,
    private zone: NgZone,
  ) {
    this.setupDeepLinks();
    this.setupSettings();

    this.allSubs.push(
      this.themeService.theme$.subscribe(theme => {
        // Ensure the document body is available before modifying classList
        if (this.document?.body) {
          const body = this.document.body;

          if (theme === 'light') {
            this.renderer.addClass(body, 'cp2-theme');
            this.renderer.removeClass(body, 'cp2-dark-theme');
          } else if (theme === 'dark') {
            this.renderer.addClass(body, 'cp2-dark-theme');
            this.renderer.removeClass(body, 'cp2-theme');
          }
        } else {
          console.error('Document body is not available.');
        }
      }),
    );

    this.allSubs.push(
      this.breakpointObserver
        .observe([
          Breakpoints.HandsetPortrait,
          Breakpoints.HandsetLandscape,
          Breakpoints.Web,
          Breakpoints.Tablet,
        ])
        .subscribe(result => {
          for (const query of Object.keys(result.breakpoints)) {
            if (result.breakpoints[query]) {
              this.currentBreakpoint = this.displayNameMap.get(query) ?? '';
            }
          }
          this.showSidebar = !(
            this.currentBreakpoint === 'handsetPortrait' ||
            this.currentBreakpoint === 'handsetLandscape'
          );
        }),
    );
  }

  public async ngOnInit(): Promise<void> {
    this.allSubs.push(
      this.accessFacade.userTokens$.subscribe(async t => {
        if (t.token) {
          this.username = decodeJwt(t.token.access_token)['name'] as string;
          this.accessToken = t.token;
          // TODO: @Jose - Hier werden alle Topics Initialisiert und später Abonniert/Subscribed
          this.SSEHService.initializeMercureTopicSubscriptions(this.accessToken.access_token);
        }
      }),
    );

    this.platform.ready().then(async () => {
      this.accessFacade.loadData();
      this.deviceF.loadData();
      this.locatorsF.loadLocators();
    });

    setInterval(
      async () => {
        if (this.accessToken?.refresh_token) {
          const refreshedToken = await this.authService.refreshAuth(this.accessToken.refresh_token);
          this.accessFacade.setAccessToken(refreshedToken);
        }

        await this.checkAndRefreshToken();
      },
      5 * 60 * 1000,
    );

    await this.checkAndRefreshToken();

    // TODO: @Jose - Hier ist ein Beispiel wie du den EventBus benutzen kannst. Der EventBus kann von überall aus genutzt werden. Wichtig ist, das der Key übereinstimmt
    /*this.allSubs.push(
            this.eventBus.on('sse:periodicPing').subscribe((event) => {
                console.log(event.data);
            })
        );*/
  }

  public ngOnDestroy(): void {
    this.allSubs.forEach(s => s.unsubscribe());
  }

  public async handleSidebarButton(sidebarButton: SidebarButton): Promise<void> {
    switch (sidebarButton.name) {
      case 'settings':
        await this.navController.navigateRoot('/settings');
        break;
      case 'admin':
        await this.navController.navigateRoot('/admin');
        break;
      case 'refresh_access':
        if (this.accessToken?.refresh_token) {
          const at = await this.authService.refreshAuth(this.accessToken.refresh_token);
          this.accessFacade.setAccessToken(at);
        }
        break;
      case 'back':
        this.navController.back();
        break;
    }
  }

  private async checkAndRefreshToken() {
    setInterval(async () => {
      if (this.accessToken?.access_token) {
        const accessToken = this.accessToken.access_token;

        try {
          const decoded = decodeJwt(accessToken);
          const currentTime = Math.floor(Date.now() / 1000); // Aktuelle Zeit in Sekunden

          if (decoded.exp && decoded.exp < currentTime) {
            if (this.accessToken?.refresh_token) {
              const at = await this.authService.refreshAuth(this.accessToken.refresh_token);

              if (!at) {
                const warningStringI18n = this.translate.instant(
                  'GENERAL.snackbar_token_is_expired',
                );

                this.snackBar.open(warningStringI18n, undefined, {
                  duration: 5000,
                  panelClass: ['warning-snackbar'],
                });

                await this.router.navigateByUrl('/login');
              } else {
                this.accessFacade.setAccessToken(at);
              }
            }
          }
        } catch (error) {
          console.error(error);
        }
      }
    }, 5000);
  }

  // Private methods
  private setupDeepLinks(): void {
    App.addListener('appUrlOpen', async (event: URLOpenListenerEvent) => {
      await this.zone.run(async () => {
        const slug = event.url.split('app.cpad2.de').pop();
        if (slug && slug.includes('login/redirect')) {
          await this.authService.initializeLoginCallback(window.location.origin + slug);
        }
      });
    });
  }

  private setupSettings() {
    this.settings.loadSettings();
    this.allSubs.push(
      this.settings.settings$.subscribe((s: Settings) => {
        this.translate.use(s.language);
        this.selectedColorSchema = s.colorScheme;
        this.getColorScheme(this.selectedColorSchema);
      }),
    );
  }

  private getColorScheme(colorScheme: string) {
    this.renderer.removeClass(this.document.body, 'cp2-theme');
    this.renderer.removeClass(this.document.body, 'cp2-rostock-theme');
    this.renderer.removeClass(this.document.body, 'cp2-neutral-theme');

    if (colorScheme === 'rostock') {
      this.renderer.addClass(this.document.body, 'cp2-rostock-theme');
    } else if (colorScheme === 'neutral') {
      this.renderer.addClass(this.document.body, 'cp2-neutral-theme');
    } else {
      this.renderer.addClass(this.document.body, 'cp2-theme');
    }
  }

  public handleSidebarStateChange(isExpanded: boolean): void {
    this.showSidebar = isExpanded;
  }
}
